// Bounded bridge from SuperPlumma to ordinary, page-owned forms.
const pageLiveControlApi = (() => {
  const routes: Record<string, string> = {
    purchase: 'nyttkjop', mileage: 'kjorebok', other_sale: 'annetsalg', subscription: 'abonnement', bank: 'bank',
    inbox: 'innboks', documentation: 'dokumentasjon', project: 'prosjekter',
    transaction_list: 'allebilag', transaction_detail: 'transaksjon', manual_posting: 'fripostering', asset: 'eiendeler',
    trial_balance: 'saldobalanse', income_statement: 'resultatregnskap', reconciliation: 'regnskapsforer',
    vat_filing: 'innsendinger', payroll: 'lonn', tax_return: 'skattemelding', annual_accounts: 'arsregnskap', year_closing: 'arsavslutning',
    ai_advisor: 'aiinnsikt', budget: 'budsjett',
  }
  const create = (deps: any) => {
    let state: any = { page: '', ui: null, receipt: null, key: 0 }
    const adapters = new Map<string, any>()
    const listeners = new Set<() => void>()
    const publish = (patch: any) => { state = { ...state, ...patch, key: state.key + 1 }; listeners.forEach((fn) => fn()) }
    const snapshot = () => ({ key: state.key, ui: { page: state.page, surface: state.ui, receipt: state.receipt } })
    const register = (page: string, adapter: any) => {
      if (!Object.hasOwn(routes, page)) throw new Error('pageControl.unsupported')
      adapters.set(page, adapter)
      if (deps.getRoute() === routes[page]) publish({ page, ui: adapter.read(), receipt: null })
      return () => { if (adapters.get(page) === adapter) adapters.delete(page) }
    }
    const refresh = (page: string) => {
      const adapter = adapters.get(page)
      if (!adapter || deps.getRoute() !== routes[page]) return
      const ui = adapter.read()
      if (state.page !== page || JSON.stringify(state.ui) !== JSON.stringify(ui)) publish({ page, ui })
    }
    const waitFor = async (page: string) => {
      for (let attempt = 0; attempt < 100; attempt += 1) {
        const adapter = adapters.get(page)
        if (adapter && deps.getRoute() === routes[page]) return adapter
        await new Promise((resolve) => setTimeout(resolve, 50))
      }
      throw new Error('pageControl.unavailable')
    }
    const perform = async (step: any, question: string) => {
      const page = String(step?.page || '')
      if (!Object.hasOwn(routes, page)) throw new Error('pageControl.unsupported')
      const mutations = ['submit', 'set_status', 'manage_members', 'archive', 'delete', 'restore', 'analyze', 'create_draft']
      if (!['open', 'set_fields', 'open_record', 'select_records', 'select_address', 'calculate_route', 'open_attachment_picker', 'open_asset', 'open_review', 'open_dialog', ...mutations].includes(step.action)) throw new Error('pageControl.unsupported')
      if (mutations.includes(step.action)) {
        const quote = String(step.authorizationQuote || '').trim()
        if (quote.length < 4 || !String(question || '').includes(quote) || page === 'bank') throw new Error('pageControl.authorization')
        const planningPage = page === 'ai_advisor' || page === 'budget'
        if (!planningPage && String(deps.country?.() || '').toUpperCase() !== 'NO') throw new Error('pageControl.country')
      }
      if (page === 'purchase' && step.action === 'set_fields' && step.fields?.duplicateDecision === 'proceed') {
        const quote = String(step.authorizationQuote || '').trim()
        if (quote.length < 4 || !String(question || '').includes(quote)) throw new Error('pageControl.authorization')
      }
      if (deps.getRoute() !== routes[page]) deps.navigate(routes[page])
      const adapter = await waitFor(page)
      const result = step.action === 'open'
        ? { result: 'opened' }
        : await adapter.perform(step)
      // Let the owning React page apply state and mount a navigated detail adapter.
      await new Promise((resolve) => setTimeout(resolve, 0))
      const observedPage = Object.keys(routes).find(key => routes[key] === deps.getRoute()) || page
      const observedAdapter = await waitFor(observedPage)
      const receipt = { action: step.action, page, confirmed: true, ...(result || {}) }
      publish({ page: observedPage, ui: observedAdapter.read(), receipt })
      return receipt
    }
    return {
      routes, register, refresh, perform, snapshot, getSnapshot: snapshot,
      subscribe: (fn: () => void) => { listeners.add(fn); return () => listeners.delete(fn) },
      beginRequest: () => publish({ receipt: null }),
      dispose: () => { adapters.clear(); listeners.clear() },
    }
  }
  return { create, routes }
})()

function usePageOwnedControlAdapter(page: string, adapter: any) {
  const latest = React.useRef(adapter)
  latest.current = adapter
  React.useEffect(() => window.PlummePageLiveControlSession?.register(page, {
    read: () => latest.current.read(),
    perform: (step: any) => latest.current.perform(step),
  }), [page])
  React.useEffect(() => { window.PlummePageLiveControlSession?.refresh(page) })
}

Object.assign(globalThis, { PlummePageLiveControl: pageLiveControlApi, usePageOwnedControlAdapter })
